import React from 'react';
import { FEATURE_IDS } from '@common-reducers/DisplaySettingsReducer';

/**
 * Visibility-rule emitter for shadow-rooted React trees.
 *
 * Many Blueticks injections (`top-header`, `chat-messages-sidebar`,
 * `blueticks-panel`, …) mount with `useShadowDOM: true`. CSS rules
 * placed in the main `<head>` cannot reach those wrappers — same-root
 * boundary — so we have to ship a parallel stylesheet *inside* every
 * shadow we care about.
 *
 * This component does that. Drop it at the top of any shadow-rooted
 * React tree that contains `[data-bt-feature]` wrappers. The JSX
 * `<style>` is rendered as part of the tree → it lives in the same
 * shadow root as the wrappers, and CSS scoped to the same root can
 * see them.
 *
 * `:host-context()` lets shadow CSS react to classes on ancestors of
 * the host element — specifically the `<html>` class that
 * `DisplayVisibilityStyles` maintains based on Redux state. Same
 * gating, different visibility rule lives.
 *
 * For belt-and-braces we also emit a plain `html.x …` rule. If for
 * some reason React mounts the component into the light DOM (rare
 * but possible — some injections fall back to no-shadow when shadow
 * attachment fails), the plain rule catches the wrappers there too.
 */
// Hide rules only — the highlight visuals are applied INLINE by
// applyHighlights (see DisplayVisibilityStyles.tsx) so they take
// effect regardless of DOM context. Earlier this file also shipped a
// CSS-based highlight, but the `display: revert` clause for hidden-
// feature reveal kept breaking visible host wrappers' flex layout.
const rules = FEATURE_IDS.map((id) =>
  [
    // Shadow-DOM gating via :host-context — reads ancestor html class.
    `:host-context(html.bt-hidden-feature-${id}) [data-bt-feature="${id}"] { display: none !important; }`,
    // Light-DOM fallback for the same component (when factory landed
    // in the main document).
    `html.bt-hidden-feature-${id} [data-bt-feature="${id}"] { display: none !important; }`,
  ].join('\n'),
).join('\n');

const ShadowFeatureGuard: React.FC = () => (
  <style data-bt-visibility="shadow-guard">{rules}</style>
);

export default ShadowFeatureGuard;
